import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../config/api.config';
import { Volume2, Settings, Check, RefreshCw } from 'lucide-react';
import { textToSpeechService } from '../../services/tts/textToSpeechService';
import { useVoice } from '../../contexts/VoiceContext';

interface AdvancedVoiceSelectorProps {
  onVoiceChange?: (voice: SpeechSynthesisVoice) => void;
  className?: string;
}

// Simple voice selector - Cloud voices only (OpenAI) for both Mobile and Desktop

const AdvancedVoiceSelector: React.FC<AdvancedVoiceSelectorProps> = ({
  onVoiceChange,
  className = ''
}) => {
  const { selectedVoice, setSelectedVoice } = useVoice();
  const [isOpen, setIsOpen] = useState(false);
  const [freeVoices, setFreeVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [isTesting, setIsTesting] = useState(false);
  const [isLoadingVoices, setIsLoadingVoices] = useState(false);
  const [voicesLoadError, setVoicesLoadError] = useState<string | null>(null);

  useEffect(() => {
    // Load voices
    const loadFreeVoices = async () => {
      // Detect mobile (just for logging)
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      console.log('📱 Device type:', isMobile ? 'Mobile' : 'Desktop');

      // ALWAYS use Cloud Voices (OpenAI) for both Mobile and Desktop
      // This hides all WebSpeech voices as requested
      const cloudVoices = textToSpeechService.getCloudVoices();
      console.log('☁️ Using OpenAI Cloud Voices for all platforms:', cloudVoices.map(v => v.name));
      setFreeVoices(cloudVoices);

      // Sync with VoiceContext if no voice selected yet
      if (!selectedVoice && cloudVoices.length > 0) {
        const bestVoice = cloudVoices[0]; // Alloy
        setSelectedVoice(bestVoice);
        onVoiceChange?.(bestVoice);
        textToSpeechService.setVoice(bestVoice);
      }
    };

    loadFreeVoices();

    // No need to listen for voiceschanged anymore since we are using static Cloud voices
    return () => { };
  }, []);

  const handleFreeVoiceSelect = (voice: SpeechSynthesisVoice) => {
    // UNLOCK AUDIO ON MOBILE
    textToSpeechService.resumeAudio();

    setSelectedVoice(voice);
    onVoiceChange?.(voice);
    textToSpeechService.setVoice(voice);
    setIsOpen(false);

    // Speak welcome message
    testVoice(voice);
  };

  // Manual reload handler
  const handleReloadVoices = async () => {
    setIsLoadingVoices(true);
    setVoicesLoadError(null);

    try {
      console.log('🔄 Manually reloading voices...');

      // Just re-fetch Cloud Voices
      const cloudVoices = textToSpeechService.getCloudVoices();
      setFreeVoices(cloudVoices);

      if (cloudVoices.length > 0) {
        const bestVoice = cloudVoices[0];
        setSelectedVoice(bestVoice);
        onVoiceChange?.(bestVoice);
        textToSpeechService.setVoice(bestVoice);
      }

    } catch (error) {
      console.error('Failed to reload voices:', error);
      setVoicesLoadError('Failed to reload. Please try again.');
    } finally {
      setIsLoadingVoices(false);
    }
  };

  const testVoice = async (voice: SpeechSynthesisVoice) => {
    try {
      setIsTesting(true);
      const messages = [
        "Welcome to our Ebook! We wish you a happy learning experience.",
        "You will have interesting experiences with our Ebook."
      ];
      const testText = messages[Math.floor(Math.random() * messages.length)];

      // Detect mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      await textToSpeechService.speak(testText, {
        voice: voice,
        speed: isMobile ? 1.0 : 0.85, // Mobile must use 1.0 to avoid distortion
        pitch: isMobile ? 1.0 : 0.95,
        volume: isMobile ? 1.0 : 0.9
      });
    } catch (error) {
      console.error('Voice test failed:', error);
    } finally {
      setIsTesting(false);
    }
  };

  const getVoiceQualityBadge = (voice: SpeechSynthesisVoice) => {
    // OpenAI voices are always excellent
    if (voice.name.startsWith('OpenAI')) {
      return <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-800">Excellent</span>;
    }

    const quality = textToSpeechService.getVoiceQuality(voice);
    const colorMap = {
      excellent: 'bg-green-100 text-green-800',
      good: 'bg-blue-100 text-blue-800',
      fair: 'bg-yellow-100 text-yellow-800',
      poor: 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`text-xs px-2 py-1 rounded-full ${colorMap[quality]}`}>
        {quality}
      </span>
    );
  };

  const getProviderBadge = (voice: SpeechSynthesisVoice) => {
    const name = voice.name.toLowerCase();
    if (name.includes('openai')) {
      return <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">OpenAI</span>;
    } else if (name.includes('microsoft')) {
      return <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Microsoft</span>;
    } else if (name.includes('google')) {
      return <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">Google</span>;
    }
    return <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full">System</span>;
  };

  const currentVoiceName = selectedVoice ? selectedVoice.name : 'Select Voice';

  return (
    <div className={`relative ${className}`}>

      {/* Voice Selector */}
      <div className="relative">
        <button
          onClick={async () => {
            // UNLOCK AUDIO ON MOBILE
            textToSpeechService.resumeAudio();

            // On production, load voices on first click
            const isProduction = window.location.hostname !== 'localhost' &&
              window.location.hostname !== '127.0.0.1' &&
              !window.location.hostname.includes('localhost');

            if (isProduction && freeVoices.length === 0) {
              console.log('🌐 Production: Loading voices on user interaction...');
              handleReloadVoices();
            }
            setIsOpen(!isOpen);
          }}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Volume2 className="w-5 h-5 text-gray-500" />
            <div className="text-left">
              <div className="font-medium text-gray-900">{currentVoiceName}</div>
              <div className="text-sm text-gray-500">
                {freeVoices.length > 0
                  ? `${freeVoices.length} voices available`
                  : isLoadingVoices
                    ? 'Loading voices...'
                    : window.location.hostname !== 'localhost' &&
                      window.location.hostname !== '127.0.0.1' &&
                      !window.location.hostname.includes('localhost')
                      ? 'Click to load voices'
                      : 'Tap to select voice'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedVoice && getVoiceQualityBadge(selectedVoice)}
            {isLoadingVoices ? (
              <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
            ) : (
              <Settings className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </button>

        {/* Loading or Error State */}
        {(voicesLoadError || isLoadingVoices || freeVoices.length === 0) && (
          <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            {isLoadingVoices ? (
              <div className="flex items-center gap-2 text-yellow-800">
                <div className="animate-spin w-4 h-4 border-2 border-yellow-600 border-t-transparent rounded-full"></div>
                <span className="text-sm">Loading voices...</span>
              </div>
            ) : voicesLoadError ? (
              <div className="flex items-center justify-between">
                <div className="text-sm text-yellow-800">{voicesLoadError}</div>
                <button
                  onClick={handleReloadVoices}
                  className="flex items-center gap-1 px-3 py-1 text-sm text-yellow-800 hover:bg-yellow-100 rounded transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                  Reload
                </button>
              </div>
            ) : freeVoices.length === 0 ? (
              <div className="flex items-center justify-between">
                <div className="text-sm text-yellow-800">
                  📱 Mobile: Tap "Load Voices" to enable voice selection
                </div>
                <button
                  onClick={handleReloadVoices}
                  className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                  Load Voices
                </button>
              </div>
            ) : null}
          </div>
        )}

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
            <div className="p-2">
              {/* Reload Button in Dropdown */}
              <button
                onClick={handleReloadVoices}
                disabled={isLoadingVoices}
                className="w-full mb-2 flex items-center justify-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 ${isLoadingVoices ? 'animate-spin' : ''}`} />
                {isLoadingVoices ? 'Loading...' : 'Reload Voices'}
              </button>

              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide px-2 py-1 mb-2">
                OpenAI Neural Voices ({freeVoices.length})
              </div>

              {freeVoices.length === 0 ? (
                <div className="px-3 py-6 text-center text-gray-500">
                  <Volume2 className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm mb-2">No voices found</p>
                  <button
                    onClick={handleReloadVoices}
                    disabled={isLoadingVoices}
                    className="text-blue-600 hover:underline text-sm disabled:opacity-50"
                  >
                    Try reloading
                  </button>
                </div>
              ) : (
                freeVoices.map((voice, index) => (
                  <div
                    key={index}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md hover:bg-gray-50 transition-colors cursor-pointer ${selectedVoice === voice ? 'bg-blue-50 border border-blue-200' : ''
                      }`}
                    onClick={() => handleFreeVoiceSelect(voice)}
                  >
                    <div className="flex-1 text-left">
                      <div className="font-medium text-gray-900">{voice.name}</div>
                      <div className="text-sm text-gray-500">{voice.lang}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getProviderBadge(voice)}
                      {getVoiceQualityBadge(voice)}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // UNLOCK AUDIO ON MOBILE
                          textToSpeechService.resumeAudio();
                          testVoice(voice);
                        }}
                        disabled={isTesting}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <Volume2 className="w-4 h-4" />
                      </button>
                      {selectedVoice === voice && (
                        <Check className="w-4 h-4 text-blue-600" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Info Section */}
      {selectedVoice && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 text-blue-800">
            <Volume2 className="w-4 h-4" />
            <span className="text-sm font-medium">Selected Voice</span>
          </div>
          <div className="text-xs text-blue-600 mt-1">
            {selectedVoice.name} • {selectedVoice.lang} • Cloud (Online)
          </div>
          {/* Debug Indicator */}
          <div className="mt-2 text-[10px] text-gray-400 flex flex-col gap-1">
            <div className="flex items-center gap-1">
              API Key: {import.meta.env.VITE_OPENAI_API_KEY ? <span className="text-green-500">Present</span> : <span className="text-red-500">Missing</span>}
            </div>
            <div className="flex items-center gap-1">
              Proxy: {API_CONFIG.openai.proxyUrl ? <span className="text-green-500">Configured</span> : <span className="text-red-500">Missing</span>}
            </div>
          </div>
        </div>
      )}

      {isTesting && (
        <div className="mt-3 flex items-center gap-2 text-blue-600">
          <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
          <span className="text-sm">Testing voice...</span>
        </div>
      )}
    </div>
  );
};

export default AdvancedVoiceSelector;